import {StructuredRemarksView} from './StructuredRemarksView'
import React, {FC, useEffect, useState} from 'react'
import axios from 'axios'
import type {InspectionDto} from '../core/_models'
import {
  getInspection,
  getAuthToken,
  inspectionAttachmentViewUrl,
  inspectionAttachmentDownloadUrl,
} from '../core/_requests'

type Props = {
  visible: boolean
  onClose: () => void
  record: InspectionDto | null
}

type InspectionAttachment = NonNullable<InspectionDto['findingAttachments']>[number]

const LABEL_CLS = 'form-label fw-semibold fs-6 mb-2 text-dark'

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const MODAL_DIALOG_STYLE: React.CSSProperties = {
  maxWidth: '95%',
  width: 'auto',
  margin: '0 auto',
}

const MODAL_CONTENT_STYLE: React.CSSProperties = {
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
}

const formatFileSize = (sizeBytes?: number | null) => {
  if (!sizeBytes || sizeBytes <= 0) return '-'
  if (sizeBytes >= 1024 * 1024) return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`
  if (sizeBytes >= 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`
  return `${sizeBytes} B`
}

const getFileTypeLabel = (attachment: InspectionAttachment) => {
  const mime = String(attachment.mimeType || '').trim()
  if (mime) return mime
  const ext = String(attachment.fileName || '').split('.').pop()
  return ext ? ext.toUpperCase() : 'FILE'
}

const normalizeAttachmentUrl = (url?: string | null) => {
  if (!url) return ''
  let fullUrl = url

  if (!/^https?:\/\//i.test(url)) {
    const rawApiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '')
    const baseOrigin = rawApiUrl.endsWith('/api') && url.startsWith('/api')
      ? rawApiUrl.slice(0, -4)
      : rawApiUrl
    const path = url.startsWith('/') ? url : `/${url}`
    fullUrl = `${baseOrigin}${path}`
  }

  const token = getAuthToken()
  if (token && !fullUrl.includes('token=')) {
    fullUrl += (fullUrl.includes('?') ? '&' : '?') + `token=${encodeURIComponent(token)}`
  }
  return fullUrl
}

export const ViewInspectionModal: FC<Props> = ({visible, onClose, record}) => {
  const [inspection, setInspection] = useState<InspectionDto | null>(record)
  const [attachmentsLoading, setAttachmentsLoading] = useState(false)
  const [attachmentsError, setAttachmentsError] = useState<string | null>(null)

  useEffect(() => {
    setInspection(record)
  }, [record])

  useEffect(() => {
    let active = true

    const loadInspection = async () => {
      if (!visible || !record?.id) return
      setAttachmentsLoading(true)
      setAttachmentsError(null)
      try {
        const fresh = await getInspection(record.id)
        if (active) {
          setInspection(fresh)
        }
      } catch (error: any) {
        if (active) {
          setAttachmentsError('Failed to load attachments.')
        }
      } finally {
        if (active) {
          setAttachmentsLoading(false)
        }
      }
    }

    loadInspection()
    return () => {
      active = false
    }
  }, [visible, record?.id])

  const handleDownload = async (url: string, fileName?: string | null) => {
    const downloadUrl = normalizeAttachmentUrl(url)
    try {
      const res = await axios.get(downloadUrl, {responseType: 'blob'})
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName || 'attachment'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch {
      window.open(downloadUrl, '_blank')
    }
  }

  if (!visible || !record) return null

  const currentRecord = inspection || record
  const inspectionDateLabel =
    currentRecord.inspectionFromDate === currentRecord.inspectionToDate
      ? currentRecord.inspectionFromDate
      : `${currentRecord.inspectionFromDate} -> ${currentRecord.inspectionToDate}`
  const indexAttachments = currentRecord.attachments || []
  const findingAttachments = currentRecord.findingAttachments || []

  return (
    <div
      className='modal fade show d-flex align-items-center justify-content-center'
      tabIndex={-1}
      style={WRAP_STYLE}
    >
      <div
        className='modal-dialog modal-xl modal-dialog-centered'
        role='dialog'
        style={MODAL_DIALOG_STYLE}
      >
        <div className='modal-content bg-white text-dark' style={MODAL_CONTENT_STYLE}>
          <div className='modal-header'>
            <h5 className='modal-title text-dark'>Inspection Details</h5>
            <button type='button' className='btn-close' onClick={onClose} />
          </div>

          <div className='modal-body' style={{overflowY: 'auto'}}>
            <div className='row g-3'>
              <div className='col-md-6'>
                <label className={LABEL_CLS}>Vessel</label>
                <div className='form-control-plaintext'>{currentRecord.vesselName || '-'}</div>
              </div>

              <div className='col-md-6'>
                <label className={LABEL_CLS}>Inspection Type</label>
                <div className='form-control-plaintext'>{currentRecord.inspectionKindName || '-'}</div>
              </div>

              <div className='col-md-6'>
                <label className={LABEL_CLS}>Inspection Date</label>
                <div className='form-control-plaintext'>{inspectionDateLabel || '-'}</div>
              </div>

              <div className='col-md-6'>
                <label className={LABEL_CLS}>Inspector Name</label>
                <div className='form-control-plaintext'>
                  {currentRecord.externalInspectorName || '-'}
                </div>
              </div>

              <div className='col-md-6'>
                <label className={LABEL_CLS}>From Port</label>
                <div className='form-control-plaintext'>{currentRecord.fromPortName || '-'}</div>
              </div>

              <div className='col-md-6'>
                <label className={LABEL_CLS}>To Port</label>
                <div className='form-control-plaintext'>{currentRecord.toPortName || '-'}</div>
              </div>

              <div className='col-md-6'>
                <label className={LABEL_CLS}>Hours Onboard</label>
                <div className='form-control-plaintext'>
                  {currentRecord.hoursOnboard != null ? `${currentRecord.hoursOnboard} h` : '-'}
                </div>
              </div>

              <div className='col-12'>
                <label className={LABEL_CLS}>Remarks</label>
                <StructuredRemarksView text={currentRecord.remarks} format='html' emptyMessage='No remarks added.' />
              </div>

              {/* Attachments Section with clear differentiation */}
              <div className='col-12'>
                <label className={LABEL_CLS}>Attachments</label>
                <div className='border rounded p-3 d-flex flex-column gap-4'>
                  {attachmentsLoading ? (
                    <span className='text-muted'>Loading attachments...</span>
                  ) : attachmentsError ? (
                    <span className='text-danger'>{attachmentsError}</span>
                  ) : indexAttachments.length === 0 && findingAttachments.length === 0 ? (
                    <span className='text-muted'>No attachments available.</span>
                  ) : (
                    <>
                      {/* Section 1: Inspection Index Attachments */}
                      <div>
                        <div className='d-flex align-items-center gap-2 mb-2'>
                          <span className='badge badge-light-primary fw-bold'>Inspection Attachments (Index Level)</span>
                          <span className='text-muted fs-8'>({indexAttachments.length})</span>
                        </div>
                        {indexAttachments.length === 0 ? (
                          <div className='text-muted fs-7 ps-2'>No attachments added directly to this inspection.</div>
                        ) : (
                          <div className='table-responsive'>
                            <table className='table table-sm align-middle mb-0'>
                              <thead>
                                <tr>
                                  <th>File</th>
                                  <th>Type</th>
                                  <th>Size</th>
                                  <th>Uploaded</th>
                                  <th className='text-end'>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {indexAttachments.map((attachment) => (
                                  <tr key={`index-${attachment.id}`}>
                                    <td>
                                      <div className='fw-semibold'>{attachment.originalFileName}</div>
                                    </td>
                                    <td>{attachment.mimeType || 'FILE'}</td>
                                    <td>{formatFileSize(attachment.sizeBytes)}</td>
                                    <td>{attachment.uploadedAt ? new Date(attachment.uploadedAt).toLocaleString() : '-'}</td>
                                    <td className='text-end'>
                                      <div className='d-inline-flex gap-2'>
                                        <a
                                          className='btn btn-sm btn-light-primary'
                                          href={normalizeAttachmentUrl(
                                            attachment.viewUrl ||
                                              inspectionAttachmentViewUrl(currentRecord.id, attachment.id)
                                          )}
                                          target='_blank'
                                          rel='noreferrer'
                                        >
                                          View
                                        </a>
                                        <button
                                          type='button'
                                          className='btn btn-sm btn-light'
                                          onClick={() =>
                                            handleDownload(
                                              attachment.downloadUrl ||
                                                inspectionAttachmentDownloadUrl(currentRecord.id, attachment.id),
                                              attachment.originalFileName
                                            )
                                          }
                                        >
                                          Download
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>

                      {/* Section 2: Finding Attachments */}
                      <div>
                        <div className='d-flex align-items-center gap-2 mb-2'>
                          <span className='badge badge-light-info fw-bold'>Finding Attachments</span>
                          <span className='text-muted fs-8'>({findingAttachments.length})</span>
                        </div>
                        {findingAttachments.length === 0 ? (
                          <div className='text-muted fs-7 ps-2'>No attachments from inspection findings.</div>
                        ) : (
                          <div className='table-responsive'>
                            <table className='table table-sm align-middle mb-0'>
                              <thead>
                                <tr>
                                  <th>Finding / Source</th>
                                  <th>File</th>
                                  <th>Type</th>
                                  <th>Size</th>
                                  <th>Uploaded</th>
                                  <th>By</th>
                                  <th className='text-end'>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {findingAttachments.map((attachment, index) => (
                                  <tr key={`finding-${attachment.sourceId}-${attachment.fileName}-${index}`}>
                                    <td>
                                      <span className='badge badge-light-secondary'>{attachment.sourceName || `Finding #${attachment.sourceId}`}</span>
                                    </td>
                                    <td>
                                      <div className='fw-semibold'>{attachment.fileName}</div>
                                    </td>
                                    <td>{getFileTypeLabel(attachment)}</td>
                                    <td>{formatFileSize(attachment.sizeBytes)}</td>
                                    <td>{attachment.uploadedAt || '-'}</td>
                                    <td>{attachment.uploadedBy || '-'}</td>
                                    <td className='text-end'>
                                      <div className='d-inline-flex gap-2'>
                                        <a
                                          className='btn btn-sm btn-light-primary'
                                          href={normalizeAttachmentUrl(attachment.viewUrl)}
                                          target='_blank'
                                          rel='noreferrer'
                                        >
                                          View
                                        </a>
                                        <button
                                          type='button'
                                          className='btn btn-sm btn-light'
                                          onClick={() =>
                                            handleDownload(attachment.downloadUrl || '', attachment.fileName)
                                          }
                                        >
                                          Download
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className='modal-footer'>
            <button type='button' className='btn btn-light btn-sm' onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ViewInspectionModal
